import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    ClockIcon,
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    TrashIcon,
    ChevronUpIcon,
    ChevronDownIcon,
    EyeIcon,
} from '@heroicons/react/24/outline';
import {
    requestUndo,
    requestRedo,
    requestUndoTo,
    requestRedoTo,
    requestClearHistory,
    setHistoryPanelVisible,
} from './utils/undoRedo/undoRedoSlice';
import { HistoryEntry } from './utils/undoRedo/types';
import { formatValueForDisplay } from './utils/undoRedo/actionDescriptions';

interface HistoryPanelProps {
    className?: string;
}

export default function HistoryPanel({ className = '' }: HistoryPanelProps) {
    const dispatch = useDispatch();
    const {
        historyEntries,
        canUndo,
        canRedo,
        isUndoing,
        isRedoing,
        showHistoryPanel,
    } = useSelector((state: any) => state.undoRedo);

    const [expandedEntries, setExpandedEntries] = useState<Set<string>>(
        new Set(),
    );
    const [showDetails, setShowDetails] = useState(false);

    const handleUndo = () => {
        dispatch(requestUndo());
    };

    const handleRedo = () => {
        dispatch(requestRedo());
    };

    const handleUndoTo = (index: number) => {
        dispatch(requestUndoTo(index));
    };

    const handleRedoTo = (index: number) => {
        dispatch(requestRedoTo(index));
    };

    const handleClearHistory = () => {
        if (
            window.confirm(
                'Are you sure you want to clear all history? This action cannot be undone.',
            )
        ) {
            dispatch(requestClearHistory());
        }
    };

    const toggleEntryExpansion = (entryId: string) => {
        const newExpanded = new Set(expandedEntries);
        if (newExpanded.has(entryId)) {
            newExpanded.delete(entryId);
        } else {
            newExpanded.add(entryId);
        }
        setExpandedEntries(newExpanded);
    };

    const formatTimestamp = (timestamp: number) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return date.toLocaleDateString();
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case 'element':
                return '🔷';
            case 'screen':
                return '📱';
            case 'experience':
                return '🎯';
            case 'canvas':
                return '🎨';
            default:
                return '⚡';
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'element':
                return 'text-blue-600 bg-blue-50';
            case 'screen':
                return 'text-green-600 bg-green-50';
            case 'experience':
                return 'text-purple-600 bg-purple-50';
            case 'canvas':
                return 'text-orange-600 bg-orange-50';
            default:
                return 'text-gray-600 bg-gray-50';
        }
    };

    if (!showHistoryPanel) {
        return null;
    }

    return (
        <div
            className={`bg-white border-l border-gray-200 flex flex-col ${className}`}
        >
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <ClockIcon className="h-5 w-5 text-gray-600" />
                        <h3 className="font-semibold text-gray-900">History</h3>
                        <span className="text-sm text-gray-500">
                            ({historyEntries.length})
                        </span>
                    </div>
                    <button
                        onClick={() => dispatch(setHistoryPanelVisible(false))}
                        className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                    >
                        ×
                    </button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mb-3">
                    <button
                        onClick={handleUndo}
                        disabled={!canUndo || isUndoing}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ArrowUturnLeftIcon className="h-4 w-4" />
                        Undo
                    </button>
                    <button
                        onClick={handleRedo}
                        disabled={!canRedo || isRedoing}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-md hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ArrowUturnRightIcon className="h-4 w-4" />
                        Redo
                    </button>
                </div>

                {/* Options */}
                <div className="flex items-center justify-between text-sm">
                    <button
                        onClick={() => setShowDetails(!showDetails)}
                        className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
                    >
                        <EyeIcon className="h-4 w-4" />
                        {showDetails ? 'Hide' : 'Show'} Details
                    </button>
                    <button
                        onClick={handleClearHistory}
                        disabled={historyEntries.length === 0}
                        className="flex items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <TrashIcon className="h-4 w-4" />
                        Clear
                    </button>
                </div>
            </div>

            {/* History List */}
            <div className="flex-1 overflow-y-auto">
                {historyEntries.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                        <ClockIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No history yet</p>
                        <p className="text-sm">Actions will appear here</p>
                    </div>
                ) : (
                    <div className="p-2">
                        {/* Current State Indicator */}
                        <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-md">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span className="text-sm font-medium text-green-800">
                                    Current State
                                </span>
                                <span className="text-xs text-green-600">
                                    {formatTimestamp(Date.now())}
                                </span>
                            </div>
                        </div>

                        {/* History Entries */}
                        {historyEntries
                            .slice()
                            .reverse()
                            .map(
                                (entry: HistoryEntry, reverseIndex: number) => {
                                    const actualIndex =
                                        historyEntries.length -
                                        1 -
                                        reverseIndex;
                                    const {
                                        action,
                                        isActive,
                                        canUndoTo,
                                        canRedoTo,
                                    } = entry;
                                    const isExpanded = expandedEntries.has(
                                        action.id,
                                    );
                                    const hasChanges =
                                        action.changes.length > 0;

                                    return (
                                        <div
                                            key={action.id}
                                            className={`mb-2 border rounded-md transition-all ${
                                                isActive
                                                    ? 'border-blue-300 bg-blue-50'
                                                    : canUndoTo
                                                    ? 'border-gray-200 bg-white hover:bg-gray-50'
                                                    : 'border-gray-100 bg-gray-50 opacity-60'
                                            }`}
                                        >
                                            <div
                                                className="p-3 cursor-pointer"
                                                onClick={() => {
                                                    if (
                                                        canUndoTo &&
                                                        !isActive
                                                    ) {
                                                        handleUndoTo(
                                                            actualIndex,
                                                        );
                                                    } else if (canRedoTo) {
                                                        handleRedoTo(
                                                            actualIndex,
                                                        );
                                                    }
                                                }}
                                            >
                                                <div className="flex items-start gap-3">
                                                    {/* Category Icon */}
                                                    <div
                                                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs ${getCategoryColor(
                                                            action.category,
                                                        )}`}
                                                    >
                                                        {getCategoryIcon(
                                                            action.category,
                                                        )}
                                                    </div>

                                                    {/* Content */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                {
                                                                    action.description
                                                                }
                                                            </p>
                                                            <div className="flex items-center gap-1 ml-2">
                                                                {hasChanges &&
                                                                    showDetails && (
                                                                        <button
                                                                            onClick={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation();
                                                                                toggleEntryExpansion(
                                                                                    action.id,
                                                                                );
                                                                            }}
                                                                            className="p-0.5 rounded hover:bg-gray-200"
                                                                        >
                                                                            {isExpanded ? (
                                                                                <ChevronUpIcon className="h-4 w-4" />
                                                                            ) : (
                                                                                <ChevronDownIcon className="h-4 w-4" />
                                                                            )}
                                                                        </button>
                                                                    )}
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2 mt-1">
                                                            <span className="text-xs text-gray-500">
                                                                {formatTimestamp(
                                                                    action.timestamp,
                                                                )}
                                                            </span>
                                                            {action.targetName && (
                                                                <>
                                                                    <span className="text-xs text-gray-400">
                                                                        •
                                                                    </span>
                                                                    <span className="text-xs text-gray-600">
                                                                        {
                                                                            action.targetName
                                                                        }
                                                                    </span>
                                                                </>
                                                            )}
                                                            {hasChanges && (
                                                                <>
                                                                    <span className="text-xs text-gray-400">
                                                                        •
                                                                    </span>
                                                                    <span className="text-xs text-gray-600">
                                                                        {
                                                                            action
                                                                                .changes
                                                                                .length
                                                                        }{' '}
                                                                        change
                                                                        {action
                                                                            .changes
                                                                            .length !==
                                                                        1
                                                                            ? 's'
                                                                            : ''}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>

                                                        {/* Action Status */}
                                                        {isActive && (
                                                            <div className="mt-1">
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                                                                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                                                                    Current
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Expanded Details */}
                                                {isExpanded &&
                                                    showDetails &&
                                                    hasChanges && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                                            <div className="space-y-2">
                                                                {action.changes.map(
                                                                    (
                                                                        change,
                                                                        changeIndex,
                                                                    ) => (
                                                                        <div
                                                                            key={
                                                                                changeIndex
                                                                            }
                                                                            className="text-xs"
                                                                        >
                                                                            <div className="font-medium text-gray-700 mb-1">
                                                                                {
                                                                                    change.displayName
                                                                                }
                                                                            </div>
                                                                            <div className="flex items-center gap-2 text-gray-600">
                                                                                <span className="bg-red-50 text-red-700 px-2 py-1 rounded">
                                                                                    {formatValueForDisplay(
                                                                                        change.oldValue,
                                                                                    )}
                                                                                </span>
                                                                                <span>
                                                                                    →
                                                                                </span>
                                                                                <span className="bg-green-50 text-green-700 px-2 py-1 rounded">
                                                                                    {formatValueForDisplay(
                                                                                        change.newValue,
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    );
                                },
                            )}
                    </div>
                )}
            </div>

            {/* Loading States */}
            {(isUndoing || isRedoing) && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                    <div className="flex items-center gap-2 text-blue-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                        <span className="text-sm font-medium">
                            {isUndoing ? 'Undoing...' : 'Redoing...'}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
